import { applyPerMille, type Fcfa } from './money';

/** Taux légal : 25 pour 1 000 (2,5 %). */
export const ZAKAT_RATE_PER_MILLE = 25;

/** Poids de référence du nissab (en grammes) selon le métal retenu. */
export const NISSAB_GRAMS = { OR: 85, ARGENT: 595 } as const;
export type NissabBase = keyof typeof NISSAB_GRAMS;

/** Paramètres fixés par l'administration (back-office › Paramètres). */
export interface ZakatParameters {
  /** Seuil d'imposition en FCFA. */
  nissabFcfa: Fcfa;
  /** Cours de l'or, FCFA par gramme. */
  goldPricePerGram: Fcfa;
  /** Cours de l'argent, FCFA par gramme. */
  silverPricePerGram: Fcfa;
  /** Taux en pour mille (25 par défaut). */
  ratePerMille: number;
  /** Métal de référence ayant servi à fixer le nissab (informatif). */
  nissabBase: NissabBase;
}

/** Saisie du fidèle. Les montants sont en FCFA, l'or et l'argent en grammes (décimales acceptées). */
export interface ZakatAssets {
  savings: Fcfa;
  cash: Fcfa;
  goldGrams: number;
  silverGrams: number;
  merchandise: Fcfa;
  receivables: Fcfa;
  debts: Fcfa;
}

export interface ZakatBreakdown {
  savings: Fcfa;
  cash: Fcfa;
  goldValue: Fcfa;
  silverValue: Fcfa;
  merchandise: Fcfa;
  receivables: Fcfa;
  grossAssets: Fcfa;
  debts: Fcfa;
  netAssets: Fcfa;
  nissab: Fcfa;
  isLiable: boolean;
  ratePerMille: number;
  zakat: Fcfa;
  /** Montant manquant pour atteindre le nissab (0 si atteint). */
  missingToNissab: Fcfa;
}

export const EMPTY_ASSETS: ZakatAssets = {
  savings: 0,
  cash: 0,
  goldGrams: 0,
  silverGrams: 0,
  merchandise: 0,
  receivables: 0,
  debts: 0,
};

export class InvalidZakatInputError extends Error {
  constructor(public readonly field: string) {
    super(`Valeur invalide pour « ${field} » : un nombre positif est attendu`);
    this.name = 'InvalidZakatInputError';
  }
}

function nonNegative(field: string, value: number, integer: boolean): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) throw new InvalidZakatInputError(field);
  if (integer && !Number.isSafeInteger(value)) throw new InvalidZakatInputError(field);
  return value;
}

/** Calcule le nissab à partir d'un cours de métal. */
export function nissabFromMetal(base: NissabBase, pricePerGram: Fcfa): Fcfa {
  return Math.round(NISSAB_GRAMS[base] * pricePerGram);
}

/** Règle de base : zakat = avoirsNets >= nissab ? round(avoirsNets × 25 / 1000) : 0 */
export function zakatDue(netAssets: Fcfa, nissab: Fcfa, ratePerMille = ZAKAT_RATE_PER_MILLE): Fcfa {
  return netAssets >= nissab ? applyPerMille(netAssets, ratePerMille) : 0;
}

/** Calcul détaillé, fonction pure utilisée à l'identique par la PWA (hors ligne) et l'API. */
export function calculateZakat(assets: ZakatAssets, params: ZakatParameters): ZakatBreakdown {
  const savings = nonNegative('savings', assets.savings, true);
  const cash = nonNegative('cash', assets.cash, true);
  const goldGrams = nonNegative('goldGrams', assets.goldGrams, false);
  const silverGrams = nonNegative('silverGrams', assets.silverGrams, false);
  const merchandise = nonNegative('merchandise', assets.merchandise, true);
  const receivables = nonNegative('receivables', assets.receivables, true);
  const debts = nonNegative('debts', assets.debts, true);
  const nissab = nonNegative('nissabFcfa', params.nissabFcfa, true);
  const ratePerMille = nonNegative('ratePerMille', params.ratePerMille, false);

  const goldValue = Math.round(goldGrams * nonNegative('goldPricePerGram', params.goldPricePerGram, true));
  const silverValue = Math.round(silverGrams * nonNegative('silverPricePerGram', params.silverPricePerGram, true));

  const grossAssets = savings + cash + goldValue + silverValue + merchandise + receivables;
  const netAssets = Math.max(0, grossAssets - debts);
  const zakat = zakatDue(netAssets, nissab, ratePerMille);

  return {
    savings,
    cash,
    goldValue,
    silverValue,
    merchandise,
    receivables,
    grossAssets,
    debts,
    netAssets,
    nissab,
    isLiable: netAssets >= nissab,
    ratePerMille,
    zakat,
    missingToNissab: Math.max(0, nissab - netAssets),
  };
}

/**
 * Paramètres par défaut (cours indicatifs) utilisés tant que la PWA n'a pas
 * récupéré ceux de l'administration. Nissab sur la base de l'argent (595 g).
 */
export const DEFAULT_ZAKAT_PARAMETERS: ZakatParameters = {
  goldPricePerGram: 72_000,
  silverPricePerGram: 900,
  nissabBase: 'ARGENT',
  nissabFcfa: nissabFromMetal('ARGENT', 900),
  ratePerMille: ZAKAT_RATE_PER_MILLE,
};

/** Prochaine date anniversaire (hawl) à partir de la date du dernier paiement. */
export function nextZakatAnniversary(lastPaymentDate: Date, from: Date = new Date()): Date {
  const next = new Date(lastPaymentDate.getTime());
  while (next.getTime() <= from.getTime()) {
    next.setUTCFullYear(next.getUTCFullYear() + 1);
  }
  return next;
}
